import type { TemplateType } from '~/types/template';
import type { FiltersProps } from '@shopify/polaris';

import { IDisplayModeFilter, IProviderTabFilter, ITemplateStatusFilter } from '../types';
import { FILTER_TAB_TEMPLATES } from '~/constants/template';
import { useCallback, useMemo, useState } from 'react';

export const FILTER_DISPLAY_MODE = [
  {
    label: 'Ready-to-test templates',
    value: IDisplayModeFilter.READY_TO_TEST,
  },
];

export const FILTER_PROVIDER_SOURCE = [
  {
    label: 'Shopify',
    value: IProviderTabFilter.SHOPIFY,
  },
  {
    label: 'GemPages',
    value: IProviderTabFilter.GEMPAGES,
  },
];

export const FILTER_TEMPLATE_STATUS = [
  { label: 'Active', value: ITemplateStatusFilter.ACTIVE },
  { label: 'Draft', value: ITemplateStatusFilter.DRAFT },
  { label: 'Need republishing', value: ITemplateStatusFilter.NEED_REPUBLISHING },
];

const DEFAULT_PROVIDER_TAB: IProviderTabFilter[] = [];
const DEFAULT_STATUS: ITemplateStatusFilter[] = [];
const DEFAULT_DISPLAY_MODE: IDisplayModeFilter[] = [];
const DEFAULT_PAGE_TYPES = undefined;
const DEFAULT_QUERY_VALUE = undefined;

export interface IAssetTemplateFilterResult {
  queryValue?: string;
  pageTypes?: TemplateType[];
  providerTab: IProviderTabFilter[];
  displayMode: IDisplayModeFilter[];
  status: ITemplateStatusFilter[];
  appliedFilters: FiltersProps['appliedFilters'];
  isSelectedOnlyTabGP: boolean;
  isSelectedOnlyTabShopify: boolean;
  isSelectedDisplayAll: boolean;
  isGPStatusOnShopifyTab: boolean;
  handleProviderTabChange: (value: IProviderTabFilter[]) => void;
  handleDisplayModeChange: (value: IDisplayModeFilter[]) => void;
  handleStatusChange: (value: ITemplateStatusFilter[]) => void;
  handleFiltersClearAll: () => void;
  handleQueryValueRemove: () => void;
  handleFiltersQueryChange: (value: string) => void;
  handlePageTypeChange: (value?: TemplateType[]) => void;
  handleResetFilters: () => void;
}
export const useInitializeAssetTemplateFilter = (): IAssetTemplateFilterResult => {
  const [queryValue, setQueryValue] = useState<string | undefined>(DEFAULT_QUERY_VALUE);
  const [pageTypes, setPageTypes] = useState<TemplateType[] | undefined>(DEFAULT_PAGE_TYPES);
  const [providerTab, setProviderTab] = useState<IProviderTabFilter[]>(DEFAULT_PROVIDER_TAB);
  const [displayMode, setDisplayMode] = useState<IDisplayModeFilter[]>(DEFAULT_DISPLAY_MODE);
  const [status, setStatus] = useState<ITemplateStatusFilter[]>(DEFAULT_STATUS);

  const handleProviderTabChange = useCallback(
    (value: IProviderTabFilter[]) => setProviderTab(value),
    [],
  );
  const handleDisplayModeChange = useCallback(
    (value: IDisplayModeFilter[]) => setDisplayMode(value),
    [],
  );
  const handleStatusChange = useCallback((value: ITemplateStatusFilter[]) => setStatus(value), []);
  const handlePageTypeChange = useCallback((value?: TemplateType[]) => setPageTypes(value), []);
  const handleFiltersQueryChange = useCallback((value: string) => setQueryValue(value), []);
  const handlePageTypeRemove = useCallback(() => setPageTypes([]), []);
  const handleDisplayModeRemove = useCallback(() => setDisplayMode([]), []);
  const handleProviderTabRemove = useCallback(() => setProviderTab([]), []);
  const handleStatusRemove = useCallback(() => setStatus([]), []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(undefined), []);
  const handleFiltersClearAll = useCallback(() => {
    handlePageTypeRemove();
    handleStatusRemove();
    handleQueryValueRemove();
  }, [handlePageTypeRemove, handleStatusRemove, handleQueryValueRemove]);

  const isSelectedOnlyTabGP = useMemo(() => {
    return providerTab?.length === 1 && providerTab[0] === IProviderTabFilter.GEMPAGES;
  }, [providerTab]);
  const isSelectedOnlyTabShopify = useMemo(() => {
    return providerTab?.length === 1 && providerTab[0] === IProviderTabFilter.SHOPIFY;
  }, [providerTab]);

  const isSelectedDisplayAll = useMemo(
    () => displayMode?.[0] !== IDisplayModeFilter.READY_TO_TEST,
    [displayMode],
  );

  const isGPStatusOnShopifyTab = useMemo(() => {
    const hasGPOnlyStatus = status.some(
      (s) => s === ITemplateStatusFilter.DRAFT || s === ITemplateStatusFilter.NEED_REPUBLISHING,
    );
    return isSelectedOnlyTabShopify && hasGPOnlyStatus;
  }, [status, isSelectedOnlyTabShopify]);

  const handleResetFilters = useCallback(() => {
    setProviderTab(DEFAULT_PROVIDER_TAB);
    setStatus(DEFAULT_STATUS);
    setDisplayMode(DEFAULT_DISPLAY_MODE);
    setPageTypes(DEFAULT_PAGE_TYPES);
    setQueryValue(DEFAULT_QUERY_VALUE);
  }, []);

  const appliedFilters: FiltersProps['appliedFilters'] = useMemo(() => {
    const data: FiltersProps['appliedFilters'] = [];
    if (pageTypes?.length) {
      data.push({
        key: 'pageType',
        label: `Page type: ${disambiguateLabelPageType(pageTypes)}`,
        onRemove: handlePageTypeRemove,
      });
    }

    if (displayMode?.length) {
      data.push({
        key: 'displayMode',
        label: `Display: ${disambiguateLabelDisplayMode(displayMode)}`,
        onRemove: handleDisplayModeRemove,
      });
    }

    if (providerTab.length) {
      data.push({
        key: 'providerTab',
        label: `Template source: ${disambiguateLabelProviderTab(providerTab)}`,
        onRemove: handleProviderTabRemove,
      });
    }

    if (status.length) {
      data.push({
        key: 'status',
        label: `Status: ${disambiguateLabelStatus(status)}`,
        onRemove: handleStatusRemove,
      });
    }

    return data;
  }, [
    pageTypes,
    providerTab,
    displayMode,
    status,
    handleDisplayModeRemove,
    handlePageTypeRemove,
    handleProviderTabRemove,
    handleStatusRemove,
  ]);

  return {
    queryValue,
    pageTypes,
    providerTab,
    displayMode,
    status,
    appliedFilters,
    isSelectedOnlyTabGP,
    isSelectedOnlyTabShopify,
    isSelectedDisplayAll,
    isGPStatusOnShopifyTab,
    handleResetFilters,
    handleDisplayModeChange,
    handleFiltersClearAll,
    handleProviderTabChange,
    handleFiltersQueryChange,
    handlePageTypeChange,
    handleQueryValueRemove,
    handleStatusChange,
  };

  function disambiguateLabelPageType(pageTypes: TemplateType[] | undefined) {
    return (FILTER_TAB_TEMPLATES.filter((item) => pageTypes?.includes(item.value as any))
      .map((item) => item.label)
      .join(', ') || 'Page type') as string;
  }
  function disambiguateLabelDisplayMode(displayModes: IDisplayModeFilter[]) {
    return (FILTER_DISPLAY_MODE.filter((item) => displayModes?.includes(item.value))
      .map((item) => item.label)
      .join(', ') || 'Display') as string;
  }
  function disambiguateLabelProviderTab(providerTab: IProviderTabFilter[]) {
    return (FILTER_PROVIDER_SOURCE.filter((item) => providerTab.includes(item.value))
      .map((item) => item.label)
      .join(', ') || 'Template source') as string;
  }
  function disambiguateLabelStatus(statuses: ITemplateStatusFilter[]) {
    return (FILTER_TEMPLATE_STATUS.filter((item) => statuses.includes(item.value))
      .map((item) => item.label)
      .join(', ') || 'Status') as string;
  }
};

// export type IInitializeTemplateFilter = ReturnType<typeof useInitializeTemplateFilter>;
